/**
 * MarketingPage — main marketing page with 7-tab layout + AI Copilot panel.
 *
 * Route: /marketing (feature-gated: marketing)
 * Tabs: Dashboard, Campaigns, Analytics, Audiences, Creatives, Conversions, Settings
 * Copilot: Resizable side panel with AI marketing specialist
 *
 * The page header and tab bar are sticky so they remain visible while
 * tab content scrolls beneath them.
 *
 * Tab, channel filter, and campaign selection sync to search params
 * (?tab=&channel=&campaigns=) so analysis views deep-link (spec §2.5.6):
 * params seed the store once on mount, then changes are reflected back
 * with history replacement.
 */

import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { MarketingDashboard } from './marketing/MarketingDashboard'
import { CampaignList } from './marketing/CampaignList'
import { AnalyticsDashboard } from './marketing/AnalyticsDashboard'
import { AudienceManager } from './marketing/AudienceManager'
import { CreativeLibrary } from './marketing/CreativeLibrary'
import { ConversionLog } from './marketing/ConversionLog'
import { MarketingSettings } from './marketing/MarketingSettings'
import { CopilotPanel } from './marketing/CopilotPanel'
import { ChannelFilter } from '@/components/marketing/ChannelFilter'
import { useCopilotStore } from '@/stores/copilotStore'
import { useMarketingStore } from '@/stores/marketingStore'
import { useMarketingT } from '@/i18n/marketing'
import { useFeature } from '@/hooks/useFeature'

const TAB_VALUES = ['dashboard', 'campaigns', 'analytics', 'audiences', 'creatives', 'conversions', 'settings']

export function MarketingPage() {
  const t = useMarketingT()
  const copilotEnabled = useFeature('marketing_copilot')
  const isOpen = useCopilotStore((s) => s.isOpen) && copilotEnabled
  const selectedChannel = useMarketingStore((s) => s.selectedChannel)
  const connectedChannels = useMarketingStore((s) => s.connectedChannels)
  const setSelectedChannel = useMarketingStore((s) => s.setSelectedChannel)
  const selectedCampaignIds = useMarketingStore((s) => s.selectedCampaignIds)

  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState(() => {
    const fromUrl = searchParams.get('tab')
    return fromUrl && TAB_VALUES.includes(fromUrl) ? fromUrl : 'dashboard'
  })

  // Seed the store from a deep link once on mount.
  useEffect(() => {
    const channel = searchParams.get('channel')
    if (channel === 'meta' || channel === 'google') setSelectedChannel(channel)
    const campaigns = searchParams.get('campaigns')
    if (campaigns) {
      useMarketingStore.getState().setSelectedCampaignIds(campaigns.split(',').filter(Boolean))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reflect tab/channel/selection back into the URL, merging over any params
  // we don't own (e.g. utm_*). Skips the mount render so a bare /marketing
  // visit stays clean until something changes — the store is global, so a
  // selection surviving from an earlier visit is shown by the chip but only
  // reaches the URL on the next change.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('tab')
      next.delete('channel')
      next.delete('campaigns')
      if (tab !== 'dashboard') next.set('tab', tab)
      if (selectedChannel) next.set('channel', selectedChannel)
      if (selectedCampaignIds.length > 0) next.set('campaigns', selectedCampaignIds.join(','))
      return next
    }, { replace: true })
  }, [tab, selectedChannel, selectedCampaignIds, setSearchParams])

  return (
    <div className="flex h-full -mx-4 lg:-mx-6 -my-4 lg:-my-6">
      {/* Main content area */}
      <div className={`flex-1 min-w-0 px-4 lg:px-6 py-4 lg:py-6 overflow-y-auto transition-all ${isOpen ? 'w-[60%]' : 'w-full'}`}>
        <PageFrame width="list">
        <Tabs value={tab} onValueChange={setTab}>
          {/* Sticky header — title + tab bar */}
          <PageHeader
            title={t('dashboard.title')}
            sticky
            bleedX
            subtitle={t('dashboard.subtitle')}
            actions={
              <ChannelFilter
                value={selectedChannel}
                onChange={setSelectedChannel}
                connectedChannels={connectedChannels}
              />
            }
          >
            <TabsList>
              <TabsTrigger value="dashboard">{t('tabs.dashboard')}</TabsTrigger>
              <TabsTrigger value="campaigns">{t('tabs.campaigns')}</TabsTrigger>
              <TabsTrigger value="analytics">{t('tabs.analytics')}</TabsTrigger>
              <TabsTrigger value="audiences">{t('tabs.audiences')}</TabsTrigger>
              <TabsTrigger value="creatives">{t('tabs.creatives')}</TabsTrigger>
              <TabsTrigger value="conversions">{t('tabs.conversions')}</TabsTrigger>
              <TabsTrigger value="settings">{t('tabs.settings')}</TabsTrigger>
            </TabsList>
          </PageHeader>

          {/* Tab content */}
          <div className="pt-5 pb-8">
            <TabsContent value="dashboard" className="mt-0">
              <MarketingDashboard />
            </TabsContent>

            <TabsContent value="campaigns" className="mt-0">
              <CampaignList />
            </TabsContent>

            <TabsContent value="analytics" className="mt-0">
              <AnalyticsDashboard />
            </TabsContent>

            <TabsContent value="audiences" className="mt-0">
              <AudienceManager />
            </TabsContent>

            <TabsContent value="creatives" className="mt-0">
              <CreativeLibrary />
            </TabsContent>

            <TabsContent value="conversions" className="mt-0">
              <ConversionLog />
            </TabsContent>

            <TabsContent value="settings" className="mt-0">
              <MarketingSettings />
            </TabsContent>
          </div>
        </Tabs>
        </PageFrame>
      </div>

      {/* Copilot panel (side panel when open, floating button when closed) */}
      {copilotEnabled && (isOpen ? (
        <div className="w-[40%] min-w-[320px] max-w-[500px] h-full shrink-0">
          <CopilotPanel />
        </div>
      ) : (
        <CopilotPanel />
      ))}
    </div>
  )
}
